import {SubmitHandler} from "./submit-handler.js";
import {HiddenElement} from "../../element-types/index.js";
import { OptionTools } from "../../element-types/option-tools.js";
import UTMHandler from "../../TiberJsUtm.js";

/**
 * Handles form submission by providing default event listeners and functionality to process
 * and send form data to a designated server endpoint.
 *
 * This class extends `SubmitHandler` and specializes in managing HTML form submissions
 * triggered by the `submit` event. It encapsulates logic to prevent default form submission behavior,
 * encode form data, and make a POST request to the server with encoded form information.
 *
 * The form is identified using its `formId`, and the submit button type defaults to "submit" unless
 * the form is not found or an error occurs during initialization.
 */
export class FormSubmitHandler extends SubmitHandler {
    formId;
    thankYouUrl;
    buttonType = "submit";

    /**
     * Constructs an instance of the class with the provided form data, parent element, collection, and form ID.
     *
     * @param {Object} formData - The data associated with the form.
     * @param {HTMLElement} parentElement - The parent DOM element where the form is appended.
     * @param {Array} collection - The collection of items related to the form.
     * @param {string} formId - The unique identifier for the form.
     * @return {Object} An instance of the class.
     */
    constructor(formData, parentElement, collection, formId) {
        super(formData, parentElement, collection);

        this.formId = formId;
    }

    /**
     * Attaches the default event listeners to the given component, including
     * a handler for the `submit` event on the form element associated with this instance.
     *
     * @param {Object} component - The component to which the default listeners are being attached.
     * @return {void} This method does not return a value.
     */
    defaultListeners(component) {
        super.defaultListeners(component);

        const wrapper = document.getElementById(this.formId);
        const form = (wrapper.shadowRoot ?? wrapper).querySelector('form');

        if (form == null) {
            this.submitButton.type = "button";
            console.error('Unable to find form to attach submit handler to.');
            return;
        }

        form.addEventListener('submit',  event => {
            event.preventDefault();
            this.submitForm(event);
        });
    }

    /**
     * Submits a form by sending a POST request to the specified URL with the form data encoded.
     *
     * @param {Event} event The event triggered by the form submission, typically a submit event.
     * @return {void} Does not return a value, but logs the success or error response to the console.
     */
    submitForm(event) {
        const utmData = UTMHandler.getUTMCodes();

        const formData = new FormData(event.target);
        Object.entries(utmData).forEach(([key, value]) => {
            if (!formData.has(key)) {
                formData.append(key, value);
            }
        });

        //to combine the countrycode and phone number, remove the separate form objects and combine the two 
        const keysToDelete = []; 
        for (const [key, value] of formData.entries()) {
            if (key.endsWith("_countryCode")) {
                const baseKey = key.replace("_countryCode", "");
                const phoneNumber = formData.get(`${baseKey}_phoneNumber`);
                if (phoneNumber !== null) {
                    formData.set(baseKey, `${value}${phoneNumber}`);
                    keysToDelete.push(key, `${baseKey}_phoneNumber`);
                }
            }
        }

        keysToDelete.forEach(key => formData.delete(key));

        const encodedData = new URLSearchParams(formData).toString();

        fetch(this.url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
            },
            body: encodedData,
        })
            .then(response => {
                if (!response.ok) {
                    return response.json().then(errorData => {
                        throw {
                            status: response.status,
                            errorData,
                        };
                    });
                }
                return response.text();
            })
            .then(data => {
                let thankYouUrl = this.getThankYouUrl(formData);
                if (thankYouUrl != null) {
                    document.location.href = thankYouUrl;
                } else {
                    document.location.reload();
                }
            })
            .catch(error => {
                alert('Unable to submit form. Please try again.');
                if (error.errorData?.token) {
                    this.setToken(error.errorData.token);
                    error.errorData.token = "Redacted";
                }

                console.error('Error:', error);
            });
    }

    /**
     * Sets the token value if the corresponding element exists and is valid.
     *
     * @param {string} token The token value to be set.
     * @return {void}
     */
    setToken(token) {
        const forgeToken = this.collection?.parentForm?.forgeToken;
        if (forgeToken instanceof HiddenElement) {
            forgeToken.dataValue = token;
        }
    }

    /**
     * Retrieves the "thank you" URL based on the given form data and internal configuration.
     * The method supports handling the URL as a string, an object with mapping logic,
     * or returns null if no valid URL is defined.
     *
     * @param {Object} formData - The form data object passed to determine the appropriate URL.
     *                             It is utilized when the "thank you" URL depends on specific conditions.
     * @return {string|null} - The resolved "thank you" URL as a string, or null if no valid URL is available.
     */
    getThankYouUrl(formData) {
        if (typeof this.thankYouUrl === 'string') {
            return this.thankYouUrl;
        }

        if (typeof this.thankYouUrl !== 'object' || this.thankYouUrl?.dependsOn == null) {
            return null;
        }

        const {dependsOn, default: defaultUrl, map} = this.thankYouUrl;
        const dependsOnValue = OptionTools.postOptionsFilterDepends({
            postOptionsFilter: {dependsOn},
            formData,
            parent: this.collection?.parentForm
        });

        return map?.[dependsOnValue] || defaultUrl;
    }
}
